// src/pages/SettingsSections.jsx
// The client's own settings in the Settings tab (3.30.0), each behind its
// own permission (Roles & Access): Incoming Fax Emails, Fax Lines, Cover
// Sheet, Default Line, Blocked Numbers, Notification Emails. The backend is routes/portal.js /settings/client.
import { useState, useEffect } from 'react';
import { RichText } from '../components/EmailEditorParts';
import { MAX_CHARS, fromPlain, toText, countChars, cleanPaste } from '../utils/coverMessage';
import { formatFax } from '../utils/format';
import { emoLabel } from '../components/Emo';
import OfficeHoursFields, { hoursProblem } from '../components/OfficeHoursFields';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { formatDate } from '../utils/format';
import { fetchSendTimings, saveSendTimings } from '../utils/api';
import { rememberSendTimings } from '../hooks/useSendTimings';

const last10 = n => String(n || '').replace(/\D/g, '').slice(-10);
const list = v => [].concat(v || []).join(', ');

function Card({ title, about, children, onSave, saving, dirty, blocked }) {
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>{emoLabel(title)}</div>
      {about && <div style={S.sub}>{about}</div>}
      <div style={{ marginTop: 12 }}>{children}</div>
      {onSave && (
        <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={onSave} disabled={saving || !dirty || !!blocked} style={{ ...S.primaryBtn, opacity: saving || !dirty || blocked ? 0.55 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
          {!dirty && <span style={S.hint}>No changes</span>}
          {dirty && blocked && <span style={{ ...S.hint, color: 'var(--t-c62828)' }}>{blocked}</span>}
        </div>
      )}
    </div>
  );
}

// 1. Who gets the client's incoming faxes by email
export function IncomingEmails({ data, save, saving }) {
  const team = data.type === 'business';   // 3.59.2: the team tick (business clients)
  const [f, setF] = useState({ incoming: list(data.incoming), incomingCc: list(data.incomingCc), incomingBcc: list(data.incomingBcc), emailTeamMembers: data.emailTeamMembers !== false });
  const dirty = f.incoming !== list(data.incoming) || f.incomingCc !== list(data.incomingCc) || f.incomingBcc !== list(data.incomingBcc) || (team && f.emailTeamMembers !== (data.emailTeamMembers !== false));
  const field = (k, label, hint) => (
    <label style={S.field}><span style={S.label}>{label}</span>
      <input value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} placeholder="name@yourdomain.com, …" style={S.input} />
      {hint && <span style={S.hint}>{hint}</span>}
    </label>
  );
  return (
    <Card title="📨 Incoming Fax Emails" saving={saving} dirty={dirty}
      about={`Who gets an email about each fax your company receives (with the fax attached, or a secured preview if your company handles protected health information). Faxes carry health information, so only addresses on your own email domains can be used: ${(data.domains || []).join(', ') || 'none set — ask Fax Portal support'}.`}
      onSave={() => save({ incoming: f.incoming, incomingCc: f.incomingCc, incomingBcc: f.incomingBcc, ...(team ? { emailTeamMembers: f.emailTeamMembers } : {}) })}>
      {field('incoming', 'To', 'Separate several with commas. To, Cc or Bcc: at least one. With Bcc only, nobody sees who else got the fax.')}
      {field('incomingCc', 'Cc')}
      {field('incomingBcc', 'Bcc')}
      {/* 3.49.23: the New-in-Inbox switch is gone: every received fax is New for each person until they open it */}
      <span style={{ ...S.hint, marginTop: 10 }}>Each received fax shows as New in Inbox for each person until they open it in the portal; the email doesn’t change that.</span>
      {data.teams?.length > 0 && (
        <div style={{ ...S.hint, marginTop: 8 }}>
          Teams with their own recipients (changed in Users → Teams): {data.teams.map(t => `${t.name} → ${[list(t.incoming), t.incomingCc?.length ? `Cc ${list(t.incomingCc)}` : '', t.incomingBcc?.length ? `Bcc ${list(t.incomingBcc)}` : ''].filter(Boolean).join(', ')}`).join(' · ')}. Faxes to a team’s number go to the team’s recipients instead.
        </div>
      )}
      {/* 3.59.2 (owner): faxes to the number of a team with no email of its own */}
      {team && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: 13, color: 'var(--t-37474f)', cursor: 'pointer' }}>
          <input type="checkbox" checked={f.emailTeamMembers} onChange={e => setF({ ...f, emailTeamMembers: e.target.checked })} style={{ marginTop: 2 }} />
          <span><strong>Email each incoming fax to everyone in the team that uses the number</strong><br />
            {/* 3.59.3: the owner's approved wording */}
            {f.emailTeamMembers ? (
              <span style={S.hint}>When a fax comes to a team’s number:<br />
                • If the team has its own email (Users → Teams), it goes there.<br />
                • If not, each person in the team gets their own email.<br />
                Untick this to send those faxes to the list above instead. Faxes to numbers without a team always go to the list above. Anyone can stop their own emails in Preferences.</span>
            ) : (
              <span style={S.hint}>Off: faxes to a team’s number go to the team’s own email if it has one, otherwise to the list above.</span>
            )}</span>
        </label>
      )}
    </Card>
  );
}

// 2. Names for the client's fax lines
export function LineNames({ data, save, saving }) {
  const start = Object.fromEntries((data.lines || []).map(l => [last10(l.number), l.label || '']));
  const [labels, setLabels] = useState(start);
  const dirty = Object.keys(start).some(k => (labels[k] || '') !== start[k]);
  return (
    <Card title="📞 Fax Lines" saving={saving} dirty={dirty} onSave={() => save({ labels })}
      about="Give each of your fax numbers a name people recognise. It’s shown where a line can be picked, like Compose → Send from.">
      {(data.lines || []).map(l => (
        <label key={l.number} style={{ ...S.field, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <span style={{ ...S.label, fontFamily: 'monospace', minWidth: 150 }}>{formatFax(l.number)}</span>
          <input value={labels[last10(l.number)] || ''} onChange={e => setLabels({ ...labels, [last10(l.number)]: e.target.value })} placeholder="e.g. Billing line" maxLength={40} style={{ ...S.input, maxWidth: 320 }} />
        </label>
      ))}
      {data.teams?.length > 0 && <div style={S.hint}>Teams sending from these lines: {data.teams.map(t => `${t.name} (${formatFax(t.number)})`).join(' · ')}.</div>}
    </Card>
  );
}

// 3. Cover sheet defaults for Compose
export function CoverSheet({ data, save, saving }) {
  // 3.67.1 (owner): the message is formatted like Compose's (bold, italic,
  // underline, lists), still at most 3,000 characters
  const startHtml = data.messageHtml || fromPlain(data.message || '');
  const [f, setF] = useState({ on: data.on !== false, notice: !!data.notice, messageHtml: startHtml, contact: data.contact || '', company: data.company || '' });
  const dirty = f.on !== (data.on !== false) || f.notice !== !!data.notice || f.messageHtml !== startHtml || f.contact !== (data.contact || '') || f.company !== (data.company || '');
  const chars = countChars(f.messageHtml), over = chars > MAX_CHARS;
  // After a save the server sends the message back rebuilt; the same text
  // (and formatting) mustn't show as a change
  useEffect(() => { setF(x => (toText(x.messageHtml) === toText(startHtml) ? { ...x, messageHtml: startHtml } : x)); }, [startHtml]);
  return (
    <Card title="📄 Cover Sheet" saving={saving} dirty={dirty} blocked={over ? `Shorten the message to ${MAX_CHARS.toLocaleString()} characters to save.` : ''}
      onSave={() => save({ on: f.on, notice: f.notice, contact: f.contact, company: f.company, message: toText(f.messageHtml), messageHtml: f.messageHtml })}
      about={`What Compose starts with for everyone at your company. People can still change it for each fax.${data.autoCover !== false ? ' Faxes sent by email always get a cover sheet, with your message, company and other contact info from here, even when the cover sheet is off for Compose.' : ''}`}>
      <label style={S.check}><input type="checkbox" checked={f.on} onChange={e => setF({ ...f, on: e.target.checked })} /> Cover sheet on</label>
      <label style={S.check}><input type="checkbox" checked={f.notice} onChange={e => setF({ ...f, notice: e.target.checked })} /> Confidentiality notice ticked</label>
      <div style={S.field}><span id="cover-default-message" style={S.label}>Message <span style={{ ...S.count, ...(over ? { color: 'var(--t-c62828)', fontWeight: 700 } : {}) }}>{chars.toLocaleString()}/{MAX_CHARS.toLocaleString()}</span></span>
        <style>{'.cover-msg .et-rich ul,.cover-msg .et-rich ol{margin:0;padding-left:22px}'}</style>
        <div className="cover-msg"><RichText labelId="cover-default-message" initial={startHtml} onChange={h => setF(x => ({ ...x, messageHtml: h }))} placeholders={false} pictures={false} links={false}
          cleanPaste={cleanPaste} minHeight={110} maxHeight="50vh" /></div>
        <span style={S.hint}>Optional, e.g. “Please call us with any questions.” Bold, italic, underline and lists print on the fax.</span></div>
      <label style={S.field}><span style={S.label}>Company</span>
        <input value={f.company} onChange={e => setF({ ...f, company: e.target.value })} maxLength={120} style={S.input} placeholder="Optional: e.g. your company’s full name" /></label>
      <label style={S.field}><span style={S.label}>Other contact info <span style={S.count}>{f.contact.length}/500</span></span>
        <textarea value={f.contact} onChange={e => setF({ ...f, contact: e.target.value })} rows={2} maxLength={500} style={S.input} placeholder="Optional: e.g. your address. Name and email come from the person sending; the phone is your office number." /></label>
    </Card>
  );
}

// 4. A person's own default line
export function DefaultLine({ data, save, saving }) {
  const [v, setV] = useState(data.value || '');
  return (
    <Card title="🎯 Default Line" saving={saving} dirty={v !== (data.value || '')} onSave={() => save({ value: v })}
      about="The line Compose starts on for you. Only you are affected.">
      {(data.options || []).length > 1 ? (
        <select value={v} onChange={e => setV(e.target.value)} style={{ ...S.input, maxWidth: 360 }}>
          <option value="">Your company’s default</option>
          {data.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : <div style={S.hint}>You send from one line, so there’s nothing to pick.</div>}
    </Card>
  );
}

// 5. Numbers the client never faxes
export function BlockedNumbers({ data, save, saving }) {
  const [rows, setRows] = useState(data.list || []);
  const [draft, setDraft] = useState({ number: '', note: '' });
  const dirty = JSON.stringify(rows.map(r => [last10(r.number), r.note || ''])) !== JSON.stringify((data.list || []).map(r => [last10(r.number), r.note || '']));
  return (
    <Card title="⛔ Blocked Numbers" saving={saving} dirty={dirty} onSave={() => save({ list: rows })}
      about="Faxes to these numbers are refused, from Compose, by email and by Resend, and failed faxes to them aren’t retried. Use it for wrong or retired numbers.">
      {!rows.length && <div style={S.hint}>No blocked numbers.</div>}
      {rows.map((r, i) => (
        <div key={r.number} style={S.listRow}>
          <span style={{ fontFamily: 'monospace', minWidth: 150 }}>{formatFax(r.number)}</span>
          <input value={r.note || ''} onChange={e => setRows(rows.map((x, k) => (k === i ? { ...x, note: e.target.value } : x)))} placeholder="Why (optional)" maxLength={80} style={{ ...S.input, flex: 1 }} />
          <button onClick={() => setRows(rows.filter((_, k) => k !== i))} style={S.dangerBtn}>Remove</button>
        </div>
      ))}
      <div style={{ ...S.listRow, marginTop: 8 }}>
        <input value={draft.number} onChange={e => setDraft({ ...draft, number: e.target.value })} placeholder="Fax number" style={{ ...S.input, width: 170 }} />
        <input value={draft.note} onChange={e => setDraft({ ...draft, note: e.target.value })} placeholder="Why (optional)" maxLength={80} style={{ ...S.input, flex: 1 }} />
        <button disabled={last10(draft.number).length !== 10} onClick={() => { setRows([...rows, { number: '+1' + last10(draft.number), note: draft.note }]); setDraft({ number: '', note: '' }); }}
          style={{ ...S.actionBtn, opacity: last10(draft.number).length === 10 ? 1 : 0.5 }}>＋ Add</button>
      </div>
    </Card>
  );
}

// 6. Which fax emails the client's people get
const NOTICES = [
  ['submitted', 'Fax sent', 'when a fax goes out (also after Resend)'],
  ['queued', 'Waiting in line', 'when a fax has to wait because another fax to the same number is going out'],
  ['delivered', 'Fax delivered', 'when the receiver got it'],
  ['retrying', 'Fax will be retried', 'when it failed and an automatic retry is scheduled'],
  ['failed', 'Fax failed', 'when it failed for good — recommended to keep on'],
];
export function Notices({ data, save, saving }) {
  const [f, setF] = useState(data);
  const dirty = NOTICES.some(([k]) => f[k] !== data[k]);
  return (
    <Card title="✉️ Notification Emails" saving={saving} dirty={dirty} onSave={() => save(f)}
      about="Which emails the sender gets about their faxes, for everyone at your company. Every fax still shows in Outbox either way.">
      {NOTICES.map(([k, label, hint]) => (
        <label key={k} style={S.check}><input type="checkbox" checked={f[k] !== false} onChange={e => setF({ ...f, [k]: e.target.checked })} /> <strong>{label}</strong> <span style={S.hint}>— {hint}</span></label>
      ))}
    </Card>
  );
}

// 7. Office hours (3.61.0, owner): when our automatic jobs send for the client
export function OfficeHours({ data, save, saving }) {
  const start = { tz: data.tz, days: data.days, open: data.open, close: data.close };
  const [f, setF] = useState(start);
  const dirty = JSON.stringify(f) !== JSON.stringify(start);
  return (
    <Card title="🕘 Office Hours" saving={saving} dirty={dirty && !hoursProblem(f)} onSave={() => save(f)}
      about="When your company's offices are open. Automatic retries and faxes to hard-to-reach numbers only go out in these hours, so they reach an open office. Faxes people send go out straight away when the line is free.">
      <OfficeHoursFields value={f} onChange={setF} disabled={saving} />
      {!data.set && <div style={{ ...S.hint, marginTop: 10 }}>These are the standard hours (Mon–Fri, 8:00 AM – 5:00 PM Eastern) until you save your own.</div>}
    </Card>
  );
}

// 8. Send Confirmation (3.64.0, owner): check before a Compose fax goes
export function SendConfirm({ data, save, saving }) {
  const [on, setOn] = useState(!!data.on);
  return (
    <Card title="🛡️ Send Confirmation" saving={saving} dirty={on !== !!data.on} onSave={() => save({ on })}
      about="Makes sure each fax from Compose goes to the right receiver. Off unless you turn it on; when it’s on, it applies to everyone at your company who sends from Compose.">
      <label style={S.check}><input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} /> <strong>Ask the sender to confirm the receiver before each fax is sent</strong></label>
      <div style={S.hint}>Before the fax goes, the sender checks that the receiver’s name and fax number are right (the first page of each document is shown too), then clicks <strong>Yes, send</strong>, or <strong>Go back</strong> to correct it. Each confirmation is noted in the Activity log. Faxes sent by email and automatic retries aren’t checked.</div>
    </Card>
  );
}

// 9. Sending Timings (3.75.0, owner, 2026-11-01): Super Admins only; ONE
// set for every client (routes/sendTimings.js, lib/sendTimings.js). It
// loads and saves itself: it isn't one client's setting.
const TIMING_GROUPS = [
  { title: 'Faxes to the same number', about: 'A fax line takes one call at a time, so faxes to the same number wait in line. A random time between the two numbers is picked each time.', keys: ['afterDelivered', 'afterFailed', 'hardSpacing'] },
  { title: 'Automatic retries', about: 'A fax that fails for a temporary reason (busy line, dropped call) is tried again by itself, only in the client’s office hours. After those retries, a fax to a number that took a fax in the last 60 days keeps being tried until the end of the client’s next business day.', keys: ['retries', 'priorityRetries', 'retryFirst', 'retryNext', 'longRetryGap'] },
  // 3.78.0 (owner, Nov 3)
  { title: 'High failure congestion numbers', about: 'Numbers that are busy most of the day but get through at certain hours (Logs → Problem numbers). Their automatic retries go only in those hours, whatever the office hours; two failures in a row close that window until the next one. People’s own faxes go when they’re sent.', keys: ['congAfterDelivered', 'congAfterFailed'] },
  { title: 'Limits', about: 'Protect the receivers’ lines and your fax numbers from repeated calls that don’t get through.', keys: ['guardFailures', 'blockLimit', 'blockHours'] },
];
const showValue = v => (Array.isArray(v) ? (v[0] === v[1] ? `${v[0]}` : `${v[0]}–${v[1]}`) : `${v}`);
export function SendingTimings() {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    fetchSendTimings(getToken).then(j => { setData(j); setF(j.values); }).catch(e => setError(e.message || "Couldn't load the sending timings."));
  }, [getToken]);
  if (error) return <div style={{ ...S.card, color: 'var(--t-c62828)' }}>{error}</div>;
  if (!data || !f) return <div style={S.card}><span style={S.hint}>Loading…</span></div>;
  const fields = Object.fromEntries(data.fields.map(x => [x.key, x]));
  const num = v => (v === '' ? '' : Number(v));
  const problem = key => {
    const x = fields[key], v = f[key];
    const vals = x.range ? v : [v];
    if (vals.some(n => n === '' || !Number.isInteger(n))) return 'Use whole numbers.';
    if (vals.some(n => n < x.lo || n > x.hi)) return `Use ${x.lo} to ${x.hi}.`;
    if (x.range && v[0] > v[1]) return 'The first number can’t be more than the second.';
    return '';
  };
  const problems = data.fields.map(x => problem(x.key)).filter(Boolean);
  const dirty = JSON.stringify(f) !== JSON.stringify(data.values);
  const changed = data.fields.filter(x => JSON.stringify(f[x.key]) !== JSON.stringify(data.values[x.key]));
  async function onSave() {
    const lines = changed.map(x => `${x.label}: ${showValue(data.values[x.key])} → ${showValue(f[x.key])} ${x.unit}`);
    if (!(await confirm({ title: 'Save the sending timings?', message: `This changes sending for every client:\n\n${lines.join('\n')}`, confirmLabel: 'Save' }))) return;
    setSaving(true);
    try {
      const j = await saveSendTimings(f, getToken);
      setData(j); setF(j.values);
      rememberSendTimings(j.text);
      notify({ tone: 'success', title: 'Sending timings saved', detail: 'They apply to every client within a minute.' });
    } catch (e) { notify({ tone: 'error', title: "Couldn't save", detail: e.message }); }
    setSaving(false);
  }
  const box = (key, i) => {
    const x = fields[key];
    const v = x.range ? f[key][i] : f[key];
    const set = n => setF(cur => ({ ...cur, [key]: x.range ? cur[key].map((old, j) => (j === i ? n : old)) : n }));
    return <input type="number" inputMode="numeric" min={x.lo} max={x.hi} step={1} value={v} disabled={!data.canEdit || saving}
      aria-label={`${x.label}${x.range ? (i === 0 ? ', from' : ', to') : ''}`} onChange={e => set(num(e.target.value))} style={S.numInput} />;
  };
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>{emoLabel('⏱️ Sending Timings')}</div>
      <div style={{ ...S.note, marginTop: 8 }}><strong>These timings apply to every client.</strong> Only Super Admins see and change them. Office hours stay each client’s own (Office Hours tab).</div>
      {TIMING_GROUPS.map(g => (
        <div key={g.title} style={{ marginTop: 16 }}>
          <div style={S.groupHead}>{g.title}</div>
          <div style={S.sub}>{g.about}</div>
          {g.keys.map(key => {
            const x = fields[key], std = data.standard[key], p = problem(key);
            const atStd = JSON.stringify(f[key]) === JSON.stringify(std);
            return (
              <div key={key} style={S.timingRow}>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div style={S.label}>{x.label}</div>
                  <div style={S.hint}>Standard: {showValue(std)} {std === 1 && x.unit === 'retries' ? 'retry' : x.unit} · allowed {x.lo}–{x.hi}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: '0 1 400px', minWidth: 0 }}>
                  {box(key, 0)}{x.range && <><span style={S.hint}>to</span>{box(key, 1)}</>}
                  <span style={{ fontSize: 13, color: 'var(--t-546e7a)' }}>{x.unit}</span>
                  {data.canEdit && !atStd && <button type="button" onClick={() => setF(cur => ({ ...cur, [key]: Array.isArray(std) ? [...std] : std }))} disabled={saving} style={S.linkBtn}>Reset to standard</button>}
                </div>
                {p && <div role="alert" style={{ ...S.hint, color: 'var(--t-c62828)', flexBasis: '100%' }}>{p}</div>}
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ ...S.hint, marginTop: 14 }}>
        A wait already set for a number (for example a Hard to reach number’s next slot) keeps its time; new waits use these timings. A block keeps the length it was given. Retries already scheduled keep their time.
        {data.changedAt ? ` Last changed ${formatDate(data.changedAt)}${data.changedBy ? ` by ${data.changedBy}` : ''}.` : ' Nothing changed yet: these are the standard timings.'}
      </div>
      {data.canEdit && (
        <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={onSave} disabled={saving || !dirty || problems.length > 0} style={{ ...S.primaryBtn, opacity: saving || !dirty || problems.length ? 0.55 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
          {dirty && <button type="button" onClick={() => setF(data.values)} disabled={saving} style={S.actionBtn}>Undo changes</button>}
          <button type="button" onClick={() => setF(JSON.parse(JSON.stringify(data.standard)))} disabled={saving || JSON.stringify(f) === JSON.stringify(data.standard)} style={{ ...S.actionBtn, opacity: JSON.stringify(f) === JSON.stringify(data.standard) ? 0.55 : 1 }}>Reset all to standard</button>
          {!dirty && <span style={S.hint}>No changes</span>}
          {dirty && problems.length > 0 && <span style={{ ...S.hint, color: 'var(--t-c62828)' }}>Fix the boxes marked in red to save.</span>}
        </div>
      )}
    </div>
  );
}

const S = {
  card: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '16px 20px', marginTop: 14, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.04)', maxWidth: 820 },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--t-00695c)' },
  sub: { color: 'var(--t-607d8b)', fontSize: 13, lineHeight: 1.5, marginTop: 4 },
  hint: { color: 'var(--t-90a4ae)', fontSize: 12 },
  count: { float: 'right', fontWeight: 400, fontSize: 12, color: 'var(--t-90a4ae)' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
  label: { fontSize: 13, fontWeight: 700, color: 'var(--t-37474f)' },
  input: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--t-37474f)', marginBottom: 10, flexWrap: 'wrap' },
  listRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' },
  primaryBtn: { padding: '9px 20px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  actionBtn: { padding: '7px 14px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-b2dfdb)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  note: { background: 'var(--c-fff8e1)', border: '1px solid var(--c-ffe082)', color: 'var(--t-5d4037)', borderRadius: 8, padding: '8px 12px', fontSize: 13, lineHeight: 1.5 },
  groupHead: { fontSize: 14, fontWeight: 700, color: 'var(--t-00695c)' },
  timingRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px', padding: '10px 0', borderBottom: '1px solid var(--c-eceff1)' },
  numInput: { width: 72, padding: '6px 8px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--t-00897b)', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: '2px 4px', textDecoration: 'underline' },
  dangerBtn: { padding: '7px 12px', background: 'var(--c-ffffff)', color: 'var(--t-c62828)', border: '1.5px solid var(--c-ef9a9a)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
};
